<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import LocationsService from '@/services/care-visit/locations.service'
import LocationDialog from '@/views/care-visit/location/LocationDialog.vue'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

definePage({
  meta: {
    name: "Location",
    subject: "care_visit.menu",
    action: "location",
  },
})

const unitId = computed(() => cookieRef('selectedUnit').value ?? null)

const headers = ref([
  {
    title: 'Name',
    key: 'name',
  },

  // {
  //   title: 'Description',
  //   key: 'description',
  // },
  {
    title: 'Location Type',
    key: 'location_type',
  },
  {
    title: 'Operating Hours',
    key: 'operating_hours',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
  },
])

const filter = computed(() => {
  return {
    unit_id: unitId.value,
  }
})

const searchQuery = ref('')
const itemsPerPage = ref(10)
const pageNumber = ref(1)
const sortBy = ref()
const orderBy = ref()

const {
  isFetching,
  data: locationsData,
  execute: fetchLocations,
} = useApi(createUrl(`/care-visit/visitor-locations`, {
  query: {
    paged: 1,
    q: searchQuery,
    search: filter,
    itemsPerPage: itemsPerPage,
    page: pageNumber,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const locations = computed(() => locationsData.value?.data ?? locationsData.value)
const totalLocations = computed(() => locationsData.value?.meta?.total ?? locationsData.value?.length)

const isLocationDialogOpen = ref(false)
const location = ref()

const editLocation = item => {
  location.value = item
  isLocationDialogOpen.value = true
}

watch(() => isLocationDialogOpen.value, value => {
  if (!value) location.value = null
})

const deleteLocation = item => {
  if (confirm('Are you sure you want to delete this location?')) {
    LocationsService.delete(
      item.id,
      () => {
        fetchLocations()
      },
      () => {
        //
      },
    )
  }
}

const getVisitingHours = (start_time, end_time) => {
  if (!start_time && !end_time) return "24Hrs"
  
  if (start_time == '00:00' && end_time == '23:59') {
    return "24Hrs"
  }

  return `${start_time} - ${end_time}`
}
</script>

<template>
  <VCard elevation="3">
    <VDivider />
    <VCardText class="d-flex flex-wrap py-4 gap-4">
      <div
        class="me-3 d-flex gap-3"
        style="inline-size: 24rem;"
      >
        <!-- -->
      </div>
      <VSpacer />

      <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
        <!-- Search -->
        <div style="inline-size: 12rem;">
          <AppTextField
            v-model="searchQuery"
            class="me-3"
            placeholder="Search"
            density="compact"
          />
        </div>
        <VBtn
          variant="flat"
          prepend-icon="tabler-plus"
          :disabled="!$can('create', 'care_visit.visitor_location')"
          @click="isLocationDialogOpen = true"
        >
          Add Location
        </VBtn>
        <VIcon
          class="cursor-pointer"
          icon="tabler-refresh"
          size="1.5rem"
          :disabled="isFetching"
          @click="fetchLocations"
        />
      </div>
    </VCardText>

    <VDivider />

    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="pageNumber"
      :items-length="totalLocations"
      :items="locations ?? []"
      :headers="headers"
      fixed-header
      class="text-no-wrap"
      :loading="isFetching"
    >
      <template #item.operating_hours="{ item }">
        {{ getVisitingHours(item.operational_start_time, item.operational_end_time) }}
      </template>
      <template #item.location_type="{ item }">
        <VChip
          v-if="item?.location_type"
          size="small"
          variant="tonal"
        >
          {{ item.location_type.charAt(0).toUpperCase() + item.location_type.slice(1) }}
        </VChip>
        <span v-else>N/A</span>
      </template>
      <template #item.status="{ item }">
        <VChip
          v-if="item?.status"
          color="success"
        >
          Active
        </VChip>
        <VChip v-else>
          Inactive
        </VChip>
      </template>
      <template #item.actions="{ item }">
        <IconBtn
          :disabled="!$can('update', 'care_visit.visitor_location')"
          @click="editLocation(item)"
        >
          <VIcon icon="tabler-edit" />
        </IconBtn>
        <IconBtn
          :disabled="!$can('delete', 'care_visit.visitor_location')"
          @click="deleteLocation(item)"
        >
          <VIcon icon="tabler-trash" />
        </IconBtn>
      </template>
    </VDataTableServer>
  </VCard>

  <LocationDialog
    v-model:isDialogOpen="isLocationDialogOpen"
    :form-data="location"
    @saved="fetchLocations"
  />
</template>
